import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export type Erwaehnung = {
  id: number;
  bestellnr: string;
  shopify_order_number: string | null;
  ist_uebung: boolean;
  quelle: 'notiz' | 'kommunikation' | 'position_notiz';
  /** Nur bei quelle 'position_notiz' gesetzt — sonst null. */
  position: { id: number; titel: string } | null;
  textauszug: string;
  von: {
    username: string;
    kuerzel: string;
    name: string;
    farbe: string | null;
    symbol: string | null;
  };
  erstellt_am: string;
  gelesen_am: string | null;
};

export type ErwaehnungenResponse = {
  erwaehnungen: Erwaehnung[];
  ungelesen: number;
};

/**
 * Die Glocke des GERADE angemeldeten Nutzers.
 *
 * Der Anmeldename steckt bewusst im Schlüssel, obwohl der Server ihn gar
 * nicht als Parameter bekommt — er nimmt den aus der Sitzung. Ohne ihn
 * bliebe beim Nutzerwechsel am gemeinsamen Tablet für einen Wimpernschlag
 * die Zahl des Vorgängers stehen, weil react-query denselben Schlüssel aus
 * dem Zwischenspeicher bedient (B88, B4 dieses Pakets). Der Rundumschlag
 * in useSitzungsSync lädt zwar ohnehin alles neu, aber erst danach — der
 * Schlüssel verhindert die falsche Zahl dazwischen.
 */
export function useErwaehnungen() {
  const auth = useAuth();
  const wer = auth.data?.user.nc_username ?? null;
  return useQuery({
    queryKey: ['erwaehnungen', wer],
    queryFn: () => apiFetch<ErwaehnungenResponse>('/erwaehnungen.php'),
    enabled: wer !== null,
    // Kein eigenes Polling: der Fokus-Refetch der Anwendung reicht. Wer
    // zum Tablet zurückkommt, hat den aktuellen Stand — und ein zweites
    // Weckintervall neben dem der Detailansicht wäre ein Regime mehr, das
    // niemand überblickt.
    staleTime: 30_000
  });
}

/** Einen Eintrag oder alle als gelesen abhaken. */
export function useErwaehnungGelesen() {
  const qc = useQueryClient();
  const auth = useAuth();
  const wer = auth.data?.user.nc_username ?? null;
  return useMutation({
    mutationFn: (was: { id: number } | { alle: true }) =>
      apiFetch('/erwaehnungen/gelesen.php', {
        method: 'POST',
        body: JSON.stringify(was)
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['erwaehnungen', wer] })
  });
}
